import { FC, useState, useEffect, useRef } from 'react'
import {
  MoreHorizontal,
  Trash2,
  Edit3,
  FileText,
  Loader2,
  RefreshCw,
  PanelLeftOpen,
} from 'lucide-react'
import { Button } from '../ui/button'
import { PatientClinicalData, UnifiedFile } from '../../contexts/ChatContext'
import { cn } from '../../lib/utils'
import { RoundingBanner } from './patient-panel'

interface PatientContextHeaderProps {
  data?: PatientClinicalData | null
  /** Whether patient data is being loaded */
  isLoading?: boolean
  onEdit: () => void
  onClear: () => Promise<void>
  /** Trigger reconciliation of patient data with conversation and documents */
  onReconcile: () => void
  /** Whether reconciliation is in progress */
  isReconciling?: boolean
  /** Files being processed */
  uploadedDocuments?: UnifiedFile[]
  /** Expand patient panel to left column */
  onExpandToColumn?: () => void
}

/**
 * Skeleton loading state for patient panel
 */
const PatientPanelSkeleton: FC = () => (
  <div className="animate-pulse p-4 rounded-lg border border-border/50 bg-card">
    {/* Summary line */}
    <div className="h-4 bg-muted rounded w-3/4 mb-3" />

    {/* Badge row */}
    <div className="flex gap-2 mb-4">
      <div className="h-6 bg-muted rounded w-20" />
      <div className="h-6 bg-muted rounded w-16" />
      <div className="h-6 bg-muted rounded w-24" />
    </div>

    {/* Content skeleton */}
    <div className="space-y-2">
      <div className="h-3 bg-muted rounded w-full" />
      <div className="h-3 bg-muted rounded w-5/6" />
      <div className="h-3 bg-muted rounded w-4/6" />
    </div>
  </div>
)

/**
 * PatientContextHeader - Clinical data display panel
 *
 * Redesigned as a clean dropdown tab:
 * - Collapsed: Shows patient summary + key stats
 * - Expanded: Full clinical data view
 * - X collapses, doesn't delete
 * - Delete is in overflow menu with confirmation
 */
export const PatientContextHeader: FC<PatientContextHeaderProps> = ({
  data,
  isLoading,
  onEdit,
  onClear,
  onReconcile,
  isReconciling = false,
  uploadedDocuments = [],
  onExpandToColumn,
}) => {
  const [isAnimated, setIsAnimated] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [confirmingClear, setConfirmingClear] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Check if files are being processed
  const isProcessing = uploadedDocuments.some(f => f.status === 'uploading' || f.status === 'processing' || f.parsingStatus === 'pending')
  const processingDocs = uploadedDocuments.filter(f => f.status === 'uploading' || f.status === 'processing' || f.parsingStatus === 'pending')

  // Check if deep extraction is in progress (quick done but not complete)
  const deepExtractingFile = uploadedDocuments.find(f => f.parsingStatus === 'quick_done')
  const isDeepExtracting = !!deepExtractingFile
  const extractionPhase = deepExtractingFile?.extractionPhase

  // Get phase-specific message
  const getExtractionMessage = () => {
    switch (extractionPhase) {
      case 'extraction': return 'Extracting clinical data...'
      case 'reconciliation': return 'Reconciling extractions...'
      case 'safety_check': return 'Running safety checks...'
      default: return 'Analyzing clinical details...'
    }
  }

  // Animate on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsAnimated(true), 50)
    return () => clearTimeout(timer)
  }, [])

  // Close menu on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false)
        setConfirmingClear(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleClear = async () => {
    if (confirmingClear) {
      await onClear()
      setShowMenu(false)
      setConfirmingClear(false)
    } else {
      setConfirmingClear(true)
    }
  }

  // Early returns (AFTER all hooks per React rules)
  if (isLoading) {
    return <PatientPanelSkeleton />
  }

  if (isProcessing && !data) {
    return (
      <div className={cn(
        "sticky top-0 z-20",
        "bg-gradient-to-b from-background via-background to-background/95",
        "border-b border-border/40",
        "transition-all duration-300 ease-out",
        isAnimated ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0"
      )}>
        <div className="relative">
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
              <Loader2 className="h-4 w-4 text-primary animate-spin" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-display font-semibold text-sm">
                  Processing Patient Data
                </span>
                <span className="text-xs text-muted-foreground">
                  {processingDocs.length === 1
                    ? processingDocs[0].name
                    : `${processingDocs.length} files`}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden max-w-[200px]">
                  <div
                    className="h-full bg-primary/60 rounded-full transition-all duration-300"
                    style={{ width: `${Math.max(processingDocs[0]?.progress || 0, 10)}%` }}
                  />
                </div>
                <span className="text-[10px] text-muted-foreground">
                  Extracting clinical data...
                </span>
              </div>
            </div>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </div>
    )
  }

  // Files attached but not yet processing - show awaiting placeholder
  const hasFilesAwaiting = uploadedDocuments.length > 0 && !isProcessing && !data
  if (hasFilesAwaiting) {
    return (
      <div className={cn(
        "sticky top-0 z-20",
        "bg-gradient-to-b from-background via-background to-background/95",
        "border-b border-border/40",
        "transition-all duration-300 ease-out",
        isAnimated ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0",
        )}>
        <div className="relative">
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-muted/30 to-transparent" />
          <div className="flex items-center gap-3 px-4 py-2.5">
            <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-muted/50">
              <FileText className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <span className="text-sm text-muted-foreground">
              Clinical context will appear here...
            </span>
          </div>
        </div>
      </div>
    )
  }

  // No data and no files - don't render
  if (!data) return null

  return (
    <div className={cn(
      "sticky top-0 z-20",
      "bg-gradient-to-b from-background via-background to-background/95",
      "border-b border-border/40",
      "transition-all duration-300 ease-out",
      isAnimated ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0",
      // Constrain max height so it never takes over the viewport
    )}>
      {/* RoundingBanner for collapsed header with signals - always visible */}
      <div className="relative flex-shrink-0">
        {/* Subtle top accent line */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

        {/* Deep extraction indicator */}
        {isDeepExtracting && (
          <div className={cn(
            "flex items-center gap-2 px-4 py-1.5 text-xs",
            "bg-primary/5 border-b border-primary/20"
          )}>
            <Loader2 className="h-3 w-3 text-primary animate-spin" />
            <span className="text-primary/80">{getExtractionMessage()}</span>
          </div>
        )}

        {/* Header with RoundingBanner content and action menu */}
        <div className="flex items-start">
          <div className="flex-1">
            <RoundingBanner
              data={data}
              isExpanded={false}
              onToggle={() => {}}
            />
          </div>

          {/* Expand to left column button */}
          {onExpandToColumn && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground mt-2"
              onClick={onExpandToColumn}
              title="Expand to side panel"
            >
              <PanelLeftOpen className="h-4 w-4" />
            </Button>
          )}

          {/* Overflow menu - positioned with right margin to keep dropdown in viewport */}
          <div className="relative p-2 mr-2" ref={menuRef}>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              onClick={() => setShowMenu(!showMenu)}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>

            {/* Dropdown menu */}
            {showMenu && (
              <div className={cn(
                "absolute right-0 top-full mt-1 z-50",
                "w-52 py-1 rounded-lg",
                "bg-popover border border-border shadow-lg",
                "animate-in fade-in-0 zoom-in-95 duration-150"
              )}>
                <button
                  onClick={() => { onEdit(); setShowMenu(false) }}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-sm",
                    "hover:bg-muted transition-colors"
                  )}
                >
                  <Edit3 className="h-4 w-4 text-muted-foreground" />
                  Edit patient data
                </button>
                <button
                  onClick={() => { onReconcile(); setShowMenu(false) }}
                  disabled={isReconciling}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-sm",
                    "hover:bg-muted transition-colors",
                    isReconciling && "opacity-50 cursor-not-allowed"
                  )}
                >
                  <RefreshCw className={cn("h-4 w-4 text-muted-foreground", isReconciling && "animate-spin")} />
                  {isReconciling ? 'Reconciling...' : 'Reconcile patient data'}
                </button>
                <div className="h-px bg-border my-1" />
                <button
                  onClick={handleClear}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-sm",
                    "transition-colors",
                    confirmingClear
                      ? "bg-destructive/10 text-destructive hover:bg-destructive/20"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                >
                  <Trash2 className="h-4 w-4" />
                  {confirmingClear ? 'Click again to confirm' : 'Clear patient data'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  )
}
